import { useEffect, useRef, useState } from "react";
import { Text, Group, Stack, Paper, Box, Divider, Popover, Flex, Select } from "@mantine/core";
import {
  IconGripVertical,
  IconEye,
  IconEyeOff,
  IconGitBranch,
  IconCopy,
  IconTrash,
  IconChevronDown,
  IconX,
  IconPlus,
  IconCalendar,
  IconClock,
  IconMail,
  IconCalendarTime,
  IconCheck,
} from "@tabler/icons-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Input,
  ToggleSwitch,
  Badge,
  Tooltip,
  Button,
  DropdownMenu,
  DropdownGroup,
  DropdownRow,
} from "@onengine/component-library";
import { useFormStore, generateId } from "@/store/form-builder";
import { getUnitPlacement } from "@/components/features/FormBuilder/utils/currencyUnit";
import type { QuestionScope } from "@/store/form-builder";
import type { FormQuestion } from "../../types/form.types";
import {
  getFieldIcon,
  getSwitchableFieldTypes,
  FIELD_CATEGORIES,
  UNIT_DROPDOWN_OPTIONS,
} from "@/components/features/FormBuilder/data/fieldTypes";
import {
  describeContextCondition,
  isContextTrigger,
} from "@/components/features/FormBuilder/data/contextTriggers";
import {
  BUILDER_LABELS,
  EMPTY_OPTION_PLACEHOLDER,
  FIELD_ACTION_LABELS,
  optionLabel,
} from "@/components/features/FormBuilder/data/builderLabels";
import { ConditionalLogicEditor } from "@/components/features/FormBuilder/components/ConditionalLogic/ConditionalLogicEditor";
import { QuestionList } from "@/components/features/FormBuilder/components/FormEditor/QuestionList/QuestionList";
import { CompactQuestionRow } from "@/components/features/FormBuilder/components/QuestionCard/CompactQuestionRow";
import { useIsNarrowEditor } from "../../hooks/useIsNarrowEditor";
import { EmptyQuestionWarning } from "./EmptyQuestionWarning";
import { isEmptyQuestion } from "../Shared/findEmptyQuestions";
import { ConfirmDeleteFieldModal } from "./ConfirmDeleteFieldModal";
import styles from "@/components/features/FormBuilder/components/QuestionCard/QuestionCard.module.scss";
import { TextArea } from "@/components/ui/TextArea/TextArea";

interface Props {
  question: FormQuestion;
  scope: QuestionScope;
  index: number;
  total: number;
  dragHandleProps?: Record<string, unknown>;
  isLastChild?: boolean;
  hasConditionalChildren?: boolean;
  depth?: number;
  noMarginBottom?: boolean;
  compact?: boolean;
}

const CONDITIONAL_ACTION_LABELS: Record<string, string> = {
  show: "Shown if",
  hide: "Hidden if",
  require: "Required if",
};

// A group entry field's "Shown if"/"Hidden if"/"Required if" meta text is derived
// from its siblings' conditional rules, since a group entry field can only be
// targeted by logic authored on another field within the same group.
function getConditionalMeta(question: FormQuestion, siblingQs: FormQuestion[]): string | null {
  const parts: string[] = [];
  for (const oq of siblingQs) {
    if (oq.id === question.id || !oq.conditionalLogicEnabled) continue;
    for (const rule of oq.conditionalLogic || []) {
      if (isContextTrigger(rule.triggerQuestionId)) continue;
      if (!(rule.targetQuestionIds || []).includes(question.id)) continue;
      const actionLabel = CONDITIONAL_ACTION_LABELS[rule.action];
      if (!actionLabel) continue;
      const triggerQuestion = siblingQs.find((sq) => sq.id === rule.triggerQuestionId);
      const optionLabelById = new Map((triggerQuestion?.options || []).map((o) => [o.id, o.value]));
      const values = (rule.triggerValues || [])
        .map((v) => optionLabelById.get(v) ?? v)
        .filter(Boolean);
      if (values.length > 0) parts.push(`${actionLabel}: ${values.join(" or ")}`);
    }
  }
  const contextMeta = getContextMeta(question, siblingQs);
  if (contextMeta) parts.push(contextMeta);
  return parts.length > 0 ? parts.join("; ") : null;
}

// Context rules (e.g. provider type) may live on any question, the target itself included.
function getContextRulesTargeting(question: FormQuestion, siblingQs: FormQuestion[]) {
  return siblingQs.flatMap((oq) =>
    oq.conditionalLogicEnabled
      ? (oq.conditionalLogic || []).filter(
          (rule) =>
            isContextTrigger(rule.triggerQuestionId) &&
            (rule.targetQuestionIds || []).includes(question.id),
        )
      : [],
  );
}

function getContextMeta(question: FormQuestion, siblingQs: FormQuestion[]): string | null {
  const parts = getContextRulesTargeting(question, siblingQs).flatMap((rule) => {
    const actionLabel = CONDITIONAL_ACTION_LABELS[rule.action];
    const condition = describeContextCondition(rule);
    return actionLabel && condition ? [`${actionLabel} ${condition}`] : [];
  });
  return parts.length > 0 ? parts.join("; ") : null;
}

function CollapsedOptions({
  options,
  type,
}: {
  options: { id: string; value: string }[];
  type: "radio" | "checkbox";
}) {
  const filtered = options.filter((o) => o.value !== "Other");
  const visible = filtered.slice(0, 3);
  const remaining = filtered.length - 3;
  return (
    <>
      {visible.map((o) => (
        <Group key={o.id} gap={8} mb={4}>
          {type === "radio" ? (
            <Box w={16} h={16} style={{ borderRadius: "50%", border: "2px solid #cfd4dd" }} />
          ) : (
            <Box w={16} h={16} className={styles.checkboxIndicator} />
          )}
          <Text size="sm" c="#363d4d">
            {optionLabel(o.value)}
          </Text>
        </Group>
      ))}
      {remaining > 0 && (
        <Text size="sm" c="dimmed" mt={2}>
          +{remaining} more option{remaining > 1 ? "s" : ""}
        </Text>
      )}
    </>
  );
}

function SortableOptionItem({
  option,
  index,
  questionType,
  scope,
  questionId,
  options,
}: {
  option: { id: string; value: string };
  index: number;
  questionType: string;
  scope: QuestionScope;
  questionId: string;
  options: { id: string; value: string }[];
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option.id,
  });
  const updateQuestion = useFormStore((s) => s.updateQuestion);

  return (
    <Group
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
      }}
      gap={6}
      wrap="nowrap"
      className={styles.optionsInputContainer}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          cursor: "grab",
          padding: "0 2px",
          opacity: 0.4,
        }}
        {...attributes}
        {...listeners}
      >
        {[0, 1, 2].map((r) => (
          <Group key={r} gap={1}>
            <Box
              w={3}
              h={3}
              style={{ borderRadius: "50%", background: "var(--neutral-900,#646d7e)" }}
            />
            <Box
              w={3}
              h={3}
              style={{ borderRadius: "50%", background: "var(--neutral-900,#646d7e)" }}
            />
          </Group>
        ))}
      </Box>
      {questionType === "radio" && <Box w={14} h={14} className={styles.radioIndicator} />}
      {questionType === "checkbox" && <Box w={14} h={14} className={styles.checkboxIndicator} />}
      <Box style={{ flex: 1 }}>
        <Input
          value={option.value}
          placeholder={EMPTY_OPTION_PLACEHOLDER}
          size="small"
          disabled={option.value === "Other"}
          className={styles.optionsInput}
          onChange={(val) => {
            const o = [...options];
            o[index] = { ...o[index], value: val };
            updateQuestion(scope, questionId, { options: o });
          }}
        />
      </Box>
      {options.length > 1 && (
        <Button
          type="icon"
          variant="tertiary"
          size="small"
          iconLeft={<IconX size={16} />}
          onClick={() => {
            const isOther = option.value === "Other";
            updateQuestion(scope, questionId, {
              options: options.filter((_, j) => j !== index),
              ...(isOther && { allowOther: false }),
            });
          }}
        />
      )}
    </Group>
  );
}

export function QuestionCard({
  question: q,
  scope,
  // index,
  // total,
  dragHandleProps,
  // isLastChild,
  // hasConditionalChildren,
  depth = 0,
  noMarginBottom,
  compact,
}: Props) {
  const {
    selectedQuestionId,
    setSelectedQuestionId,
    deleteQuestion,
    duplicateQuestion,
    updateQuestion,
    getAllQuestions,
    getScopeQuestions,
    reorderOptions,
  } = useFormStore();
  const { pageId, sectionId } = scope;
  const isSelfSelected = selectedQuestionId === q.id;
  const childIds = q.type === "repeating-group" ? (q.fields || []).map((f) => f.id) : [];
  const isChildSelected = childIds.includes(selectedQuestionId || "");
  const isSelected = isSelfSelected || isChildSelected;
  const Icon = getFieldIcon(q.type);
  const [hover, setHover] = useState(false);
  const [activeOptId, setActiveOptId] = useState<string | null>(null);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const questionInputRef = useRef<HTMLDivElement>(null);
  const questionIsEmpty = isEmptyQuestion(q);
  // The labelled Properties drawer carries these actions on a phone.
  const isNarrow = useIsNarrowEditor();

  // A field just added has no question yet, so land the cursor where the user types it.
  useEffect(() => {
    if (isSelfSelected && questionIsEmpty) {
      questionInputRef.current?.querySelector("input")?.focus();
    }
  }, [isSelfSelected, questionIsEmpty]);
  const switchableFieldTypes = getSwitchableFieldTypes(scope);

  const hasPH = ![
    "date",
    "time",
    "date-time",
    "photo-upload",
    "video-upload",
    "radio",
    "checkbox",
    "dropdown",
    "acknowledgement",
    "signature",
    "repeating-group",
  ].includes(q.type);
  const hasOpts = ["radio", "checkbox", "dropdown"].includes(q.type);
  // A group entry field can only be targeted by its own siblings' logic, so scope the
  // "hidden by logic" check to the group's own fields; top-level questions keep
  // the whole-form check.
  const siblingQs = scope.groupId ? getScopeQuestions(scope) : getAllQuestions();
  const isChildOfAnswerLogic = siblingQs.some(
    (oq) =>
      oq.id !== q.id &&
      oq.conditionalLogicEnabled &&
      oq.conditionalLogic?.some(
        (r) =>
          !isContextTrigger(r.triggerQuestionId) &&
          (r.targetQuestionIds || []).includes(q.id) &&
          (r.action === "show" || r.action === "hide"),
      ),
  );
  const isHiddenByLogic =
    isChildOfAnswerLogic ||
    getContextRulesTargeting(q, siblingQs).some((r) => r.action === "show" || r.action === "hide");
  const contextMeta = getContextMeta(q, siblingQs);
  const hasChildLogic =
    q.type === "repeating-group" &&
    (q.fields || []).some(
      (f) => f.conditionalLogicEnabled && (f.conditionalLogic?.length || 0) > 0,
    );

  const optSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );
  const handleOptDragStart = (e: DragStartEvent) => setActiveOptId(e.active.id as string);
  const handleOptDragEnd = (e: DragEndEvent) => {
    setActiveOptId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const opts = q.options || [];
    const oldIdx = opts.findIndex((o) => o.id === active.id);
    const newIdx = opts.findIndex((o) => o.id === over.id);
    if (oldIdx >= 0 && newIdx >= 0) reorderOptions(scope, q.id, oldIdx, newIdx);
  };

  const COLUMN_WIDTH = 40;
  const connectorWidth = depth * COLUMN_WIDTH;

  const Connector = () => (
    <Box w={connectorWidth} style={{ position: "relative", flexShrink: 0 }}>
      {/* Horizontal branch with circle at the deepest level */}
      <svg
        style={{
          position: "absolute",
          left: (depth - 1) * COLUMN_WIDTH + 4,
          top: "calc(50% - 12px)",
          width: 36,
          height: 24,
        }}
        viewBox="0 0 36 24"
        fill="none"
      >
        <line x1="8" y1="12" x2="24" y2="12" stroke="#646d7e" strokeWidth="2" />
        <circle
          cx="28"
          cy="12"
          r="4"
          fill="var(--bg-neutral-medium,#F0F2F5)"
          stroke="#646d7e"
          strokeWidth="2"
        />
      </svg>
    </Box>
  );

  /* ── COMPACT ── */
  if (!isSelected) {
    if (compact) {
      return (
        <Group gap={0} mb={noMarginBottom ? 0 : 8} wrap="nowrap" style={{ position: "relative" }}>
          {isChildOfAnswerLogic && <Connector />}
          <CompactQuestionRow
            question={q}
            isHiddenByLogic={isHiddenByLogic}
            hasLogic={
              ((q.conditionalLogic?.length || 0) > 0 && !!q.conditionalLogicEnabled) ||
              hasChildLogic
            }
            metaText={getConditionalMeta(q, siblingQs)}
            dragHandleProps={dragHandleProps}
            onSelect={() => setSelectedQuestionId(q.id)}
          />
        </Group>
      );
    }
    return (
      <Group gap={0} mb={noMarginBottom ? 0 : 8} wrap="nowrap" style={{ position: "relative" }}>
        {isChildOfAnswerLogic && <Connector />}
        <Paper
          data-qcard
          data-question-id={q.id}
          p={0}
          radius="8px"
          withBorder
          flex={1}
          onClick={() => setSelectedQuestionId(q.id)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            display: "flex",
            cursor: "pointer",
            transition: "border-color 0.15s",
            borderColor: hover
              ? "var(--bg-brand-hover,#004ddb)"
              : isHiddenByLogic || q.hidden
                ? "var(--border-default,#cfd4dd)"
                : "var(--border-hover,#e2e6eb)",
            borderStyle: isHiddenByLogic || q.hidden ? "dashed" : "solid",
            borderWidth: isHiddenByLogic || q.hidden ? 2 : 1,
            opacity: 1,
          }}
        >
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 6px",
              cursor: "grab",
            }}
            {...dragHandleProps}
          >
            <IconGripVertical size={16} color="#afb8c5" />
          </Box>
          <Box flex={1} miw={0} p="sm" pl={4}>
            <Group gap={6} mb={6} wrap="wrap">
              <Group gap={4}>
                <Icon size={14} color="#646d7e" />
                <Text size="sm" fw={"var(--font-weight-medium,500)"} c="dimmed">
                  {q.typeName}
                </Text>
                <Text size="sm" c="var(--text-weak)" data-testid="required-state">
                  {" · "}
                  {q.required ? "Required" : "Optional"}
                </Text>
              </Group>
              {(((q.conditionalLogic?.length || 0) > 0 && q.conditionalLogicEnabled) ||
                hasChildLogic) && (
                <Badge
                  color="info"
                  variant="outline"
                  size="medium"
                  iconLeft={<IconGitBranch size={16} />}
                  className={styles.logicAppliedClass}
                >
                  Logic Applied
                </Badge>
              )}
              {q.hidden && (
                <Badge
                  color="neutral"
                  variant="lowContrast"
                  size="medium"
                  iconLeft={<IconEyeOff size={16} />}
                  className={styles.logichiddenClass}
                >
                  Hidden
                </Badge>
              )}
              {isHiddenByLogic && (
                <Badge
                  color="neutral"
                  variant="lowContrast"
                  size="medium"
                  iconLeft={<IconEyeOff size={16} />}
                  className={styles.logichiddenClass}
                >
                  Hidden by Logic
                </Badge>
              )}
            </Group>
            {contextMeta && (
              <Text size="sm" c="dimmed" mb={6} data-testid="context-logic-meta">
                {contextMeta}
              </Text>
            )}
            <Group justify="space-between" wrap="nowrap" gap={8}>
              <Text
                size="md"
                fw={"var(--font-weight-semi-bold,600)"}
                c="var(--text-default,#363d4d)"
              >
                {q.question || (
                  <Text span fw={"var(--font-weight-medium,500)"} c="var(--text-warning-default)">
                    {q.typeName} (no question yet)
                  </Text>
                )}
              </Text>
            </Group>
            {questionIsEmpty && <EmptyQuestionWarning />}
            {q.description && q.description !== " " && (
              <Text size="sm" c="dimmed" mt={3}>
                {q.description}
              </Text>
            )}
            {q.type === "repeating-group" && (
              <Text size="sm" c="dimmed" mt={3}>
                {(q.fields || []).length} field{(q.fields || []).length === 1 ? "" : "s"}
                {(q.fields || []).length > 0
                  ? `: ${(q.fields || [])
                      .map((f) => f.question || f.typeName)
                      .slice(0, 3)
                      .join(", ")}${(q.fields || []).length > 3 ? "…" : ""}`
                  : ""}
              </Text>
            )}
            {q.type === "photo-upload" && (
              <Text size="sm" c="dimmed" mt={3}>
                {q.photoSlots?.length || 1} photo{(q.photoSlots?.length || 1) > 1 ? "s" : ""}
              </Text>
            )}
            {q.type === "video-upload" && (
              <Text size="sm" c="dimmed" mt={3}>
                {q.videoSlots?.length || 1} video{(q.videoSlots?.length || 1) > 1 ? "s" : ""}
              </Text>
            )}
            <Box mt={8} style={{ pointerEvents: "none" }}>
              {["single-line-text", "email", "phone"].includes(q.type) && (
                <Input
                  placeholder={q.placeholder || "Enter your answer..."}
                  disabled
                  size="small"
                  inputClassName={styles.previewInputEl}
                  iconLeft={q.type === "email" ? <IconMail size={20} /> : undefined}
                />
              )}
              {q.type === "multi-line-text" && (
                <TextArea
                  placeholder={q.placeholder || "Enter your answer..."}
                  disabled
                  size="small"
                  inputClassName={styles.previewInputEl}
                />
              )}
              {q.type === "number" && (
                <Box className={styles.numberInputWrapper}>
                  {getUnitPlacement(q.unit) === "prefix" && (
                    <Text size="sm" className={styles.unitText}>
                      {q.unit}
                    </Text>
                  )}
                  <Input
                    placeholder={q.placeholder || "Enter your answer..."}
                    disabled
                    size="small"
                    inputClassName={styles.previewInputEl}
                  />
                  {getUnitPlacement(q.unit) === "suffix" && (
                    <Text size="sm" className={styles.unitText}>
                      {q.unit}
                    </Text>
                  )}
                </Box>
              )}
              {q.type === "radio" && <CollapsedOptions options={q.options || []} type="radio" />}
              {q.type === "checkbox" && (
                <CollapsedOptions options={q.options || []} type="checkbox" />
              )}
              {q.type === "dropdown" && (
                <Input
                  placeholder="Select an option"
                  disabled
                  size="small"
                  inputClassName={styles.previewInputEl}
                  iconRight={<IconChevronDown size={14} />}
                />
              )}
              {q.type === "signature" && (
                <Paper
                  p="md"
                  radius="8px"
                  style={{ border: "2px dashed #cfd4dd", textAlign: "center" }}
                >
                  <Text size="sm" c="dimmed">
                    {q.placeholder || "Click to sign"}
                  </Text>
                </Paper>
              )}
              {["date", "time", "date-time"].includes(q.type) && (
                <Input
                  placeholder={
                    q.type === "date"
                      ? "MM / DD / YYYY"
                      : q.type === "time"
                        ? "--:-- --"
                        : "MM / DD / YYYY  --:-- --"
                  }
                  disabled
                  size="small"
                  inputClassName={styles.previewInputEl}
                  iconRight={
                    q.type === "time" ? (
                      <IconClock size={14} />
                    ) : q.type === "date" ? (
                      <IconCalendar size={14} />
                    ) : (
                      <IconCalendarTime size={14} />
                    )
                  }
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Group>
    );
  }

  /* ── EXPANDED ── */
  return (
    <Group gap={0} mb={noMarginBottom ? 0 : 8} wrap="nowrap" style={{ position: "relative" }}>
      {isChildOfAnswerLogic && <Connector />}
      <Paper
        data-qcard
        data-question-id={q.id}
        p={0}
        pb={15}
        radius="8px"
        flex={1}
        onClick={() => setSelectedQuestionId(q.id)}
        style={{
          display: "flex",
          border: isSelfSelected ? "2px solid #004ddb" : "1px solid var(--border-default,#cfd4dd)",
          cursor: "pointer",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Flex style={{ width: "100%", alignItems: "center" }}>
          <Box
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              padding: "16px 4px 0 6px",
              cursor: "grab",
            }}
            {...dragHandleProps}
          >
            <IconGripVertical size={16} color="#afb8c5" />
          </Box>
          <Box flex={1} miw={0} p="sm" pl={4}>
            {/* Type + actions */}
            <Group justify="space-between" mb="sm">
              <Popover
                opened={typeMenuOpen}
                onChange={setTypeMenuOpen}
                position="bottom-start"
                offset={6}
                shadow="md"
                withinPortal
                middlewares={{ flip: true, shift: true }}
              >
                <Popover.Target>
                  <Paper
                    component="button"
                    px="sm"
                    py={4}
                    radius="xl"
                    withBorder
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                      fontSize: 13,
                      border: "1px solid #cfd4dd",
                      background: "white",
                      borderRadius: 8,
                    }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      if (q.type === "repeating-group") return;
                      setTypeMenuOpen((v) => !v);
                    }}
                  >
                    <Text size="xs" c="dimmed">
                      Type:
                    </Text>
                    <Text size="sm" fw={"var(--font-weight-medium,500)"}>
                      {q.typeName}
                    </Text>
                    {q.type !== "repeating-group" && <IconChevronDown size={12} color="#646d7e" />}
                  </Paper>
                </Popover.Target>
                <Popover.Dropdown p={0} style={{ background: "none", border: "none" }}>
                  <DropdownMenu width="100%" scrollable>
                    {FIELD_CATEGORIES.filter((cat) => cat !== "Coming Soon").map((cat, i) => (
                      <DropdownGroup key={cat} label={cat} showDivider={i > 0}>
                        {switchableFieldTypes
                          .filter((f) => f.category === cat)
                          .map((f) => {
                            const FI = f.icon;
                            return (
                              <DropdownRow
                                key={f.id}
                                leftSection={<FI size={14} />}
                                title={f.label}
                                active={q.type === f.id}
                                rightSection={q.type === f.id ? <IconCheck size={14} /> : null}
                                disabled={!!f.comingSoon}
                                onClick={() => {
                                  updateQuestion(scope, q.id, {
                                    type: f.id,
                                    typeName: f.label,
                                  });
                                  setTypeMenuOpen(false);
                                }}
                              />
                            );
                          })}
                      </DropdownGroup>
                    ))}
                  </DropdownMenu>
                </Popover.Dropdown>
              </Popover>
              {!isNarrow && (
                <Group gap={"8px"}>
                  <Tooltip content={FIELD_ACTION_LABELS.visibility}>
                    <Button
                      type="icon"
                      variant="tertiary"
                      size="small"
                      iconLeft={q.hidden ? <IconEyeOff size={16} /> : <IconEye size={16} />}
                      htmlAttributes={{
                        "aria-label": FIELD_ACTION_LABELS.visibility,
                        "aria-pressed": !!q.hidden,
                      }}
                      onClick={(e) => {
                        e?.stopPropagation();
                        updateQuestion(scope, q.id, { hidden: !q.hidden });
                      }}
                    />
                  </Tooltip>
                  {depth < 3 && (
                    <Tooltip content={FIELD_ACTION_LABELS.conditionalLogic}>
                      <Button
                        type="icon"
                        variant={q.conditionalLogicEnabled ? "secondary" : "tertiary"}
                        size="small"
                        iconLeft={<IconGitBranch size={16} />}
                        htmlAttributes={{
                          "aria-label": FIELD_ACTION_LABELS.conditionalLogic,
                          "aria-pressed": !!q.conditionalLogicEnabled,
                        }}
                        className={q.conditionalLogicEnabled ? styles.conditionButtonEnabled : ""}
                        onClick={(e) => {
                          e?.stopPropagation();
                          if (q.conditionalLogicEnabled)
                            updateQuestion(scope, q.id, {
                              conditionalLogicEnabled: false,
                              conditionalLogic: [],
                            });
                          else
                            updateQuestion(scope, q.id, {
                              conditionalLogicEnabled: true,
                            });
                        }}
                      />
                    </Tooltip>
                  )}
                  <Tooltip content={FIELD_ACTION_LABELS.duplicate}>
                    <Button
                      type="icon"
                      variant="tertiary"
                      size="small"
                      iconLeft={<IconCopy size={16} />}
                      htmlAttributes={{ "aria-label": FIELD_ACTION_LABELS.duplicate }}
                      onClick={(e) => {
                        e?.stopPropagation();
                        duplicateQuestion(scope, q.id);
                      }}
                    />
                  </Tooltip>
                  <Tooltip content={FIELD_ACTION_LABELS.delete}>
                    <Button
                      type="icon"
                      variant="tertiary"
                      size="small"
                      iconLeft={<IconTrash size={16} />}
                      htmlAttributes={{ "aria-label": FIELD_ACTION_LABELS.delete }}
                      onClick={(e) => {
                        e?.stopPropagation();
                        setDeleteConfirmOpen(true);
                      }}
                    />
                  </Tooltip>
                </Group>
              )}
            </Group>

            <Box
              ref={questionInputRef}
              onClick={(e) => e.stopPropagation()}
              mb={q.description && q.description !== " " ? 4 : 12}
            >
              <Input
                value={q.question}
                onChange={(val) => updateQuestion(scope, q.id, { question: val })}
                placeholder={BUILDER_LABELS.emptyQuestionPlaceholder}
                size="small"
              />
              {questionIsEmpty && <EmptyQuestionWarning />}
            </Box>
            {q.description && q.description !== " " && (
              <Text size="sm" c="dimmed" mb={12}>
                {q.description}
              </Text>
            )}

            {hasPH && (
              <Box onClick={(e) => e.stopPropagation()} mb={12}>
                <Text
                  size="sm"
                  fw={"var(--font-weight-semi-bold,600)"}
                  c="var(--text-default,#363d4d)"
                  mb={4}
                >
                  Placeholder Text
                </Text>
                <Input
                  value={q.placeholder || ""}
                  onChange={(val) => updateQuestion(scope, q.id, { placeholder: val })}
                  placeholder="Enter your answer..."
                  size="small"
                />
              </Box>
            )}

            {hasOpts && (
              <Stack gap={6} mb={12} onClick={(e) => e.stopPropagation()}>
                <DndContext
                  sensors={optSensors}
                  collisionDetection={closestCenter}
                  onDragStart={handleOptDragStart}
                  onDragEnd={handleOptDragEnd}
                >
                  <SortableContext
                    items={(q.options || []).filter((o) => o.value !== "Other").map((o) => o.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {(q.options || [])
                      .filter((opt) => opt.value !== "Other")
                      .map((opt) => {
                        const realIndex = (q.options || []).findIndex((o) => o.id === opt.id);
                        return (
                          <SortableOptionItem
                            key={opt.id}
                            option={opt}
                            index={realIndex}
                            questionType={q.type}
                            scope={scope}
                            questionId={q.id}
                            options={q.options || []}
                          />
                        );
                      })}
                  </SortableContext>
                  <DragOverlay dropAnimation={{ duration: 150, easing: "ease" }}>
                    {activeOptId &&
                      (() => {
                        const opt = (q.options || []).find((o) => o.id === activeOptId);
                        if (!opt) return null;
                        return (
                          <Paper p="xs" radius="md" withBorder shadow="sm">
                            <Text size="sm">{optionLabel(opt.value)}</Text>
                          </Paper>
                        );
                      })()}
                  </DragOverlay>
                </DndContext>
                {q.allowOther && (
                  <Group
                    gap={6}
                    wrap="nowrap"
                    justify="space-between"
                    mt={2}
                    className={styles.otherOptionContainer}
                  >
                    {q.type === "radio" && <Box w={14} h={14} className={styles.radioIndicator} />}
                    {q.type === "checkbox" && (
                      <Box w={14} h={14} className={styles.checkboxIndicator} />
                    )}
                    <Box className={styles.otherOptionLabel}>
                      <Text size="sm" c="dimmed" fs="italic">
                        Other (text input)
                      </Text>
                    </Box>
                    <Button
                      type="icon"
                      variant="tertiary"
                      size="small"
                      iconLeft={<IconX size={16} />}
                      onClick={() =>
                        updateQuestion(scope, q.id, {
                          allowOther: false,
                          options: (q.options || []).filter((o) => o.value !== "Other"),
                        })
                      }
                    />
                  </Group>
                )}
                <Group gap={6} mt={2}>
                  <Text
                    component="button"
                    size="sm"
                    fw={"var(--font-weight-semi-bold,600)"}
                    c="var(--bg-brand-default,#004ddb)"
                    onClick={() => {
                      updateQuestion(scope, q.id, {
                        options: [...(q.options || []), { id: generateId("o"), value: "" }],
                      });
                    }}
                    style={{
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <IconPlus size={14} /> New Option
                  </Text>
                  {!q.allowOther && (
                    <>
                      <Text size="sm" c="dimmed">
                        or
                      </Text>
                      <Text
                        component="button"
                        size="sm"
                        fw={"var(--font-weight-semi-bold,600)"}
                        c="var(--bg-brand-default,#004ddb)"
                        onClick={() =>
                          updateQuestion(scope, q.id, {
                            options: [
                              ...(q.options || []),
                              { id: generateId("o"), value: "Other" },
                            ],
                            allowOther: true,
                          })
                        }
                        style={{ border: "none", background: "transparent", cursor: "pointer" }}
                      >
                        Add Other
                      </Text>
                      <Text size="sm" c="dimmed">
                        option
                      </Text>
                    </>
                  )}
                </Group>
                <Divider color=" var(--neutral-500,#e2e6eb)" />
                {q.type === "dropdown" && (
                  <Group gap={8}>
                    <ToggleSwitch
                      checked={q.allowMultiple || false}
                      onChange={(next) => updateQuestion(scope, q.id, { allowMultiple: next })}
                      size="sm"
                      intent="brand"
                    />
                    <Text size="sm" c="var(--text-default,#363d4d)">
                      Allow multiple selections
                    </Text>
                  </Group>
                )}
              </Stack>
            )}

            {q.type === "number" && (
              <Box mb={12} onClick={(e) => e.stopPropagation()}>
                <Text
                  size="sm"
                  fw={"var(--font-weight-semi-bold,600)"}
                  c="var(--text-default,#363d4d)"
                  mb={4}
                >
                  {BUILDER_LABELS.unit}
                </Text>
                <Select
                  aria-label={BUILDER_LABELS.unit}
                  searchable
                  value={q.unit || "none"}
                  onChange={(val) => updateQuestion(scope, q.id, { unit: val ?? "none" })}
                  data={UNIT_DROPDOWN_OPTIONS}
                  size="sm"
                  radius="md"
                  comboboxProps={{ width: 200, position: "bottom-start" }}
                />
              </Box>
            )}

            {q.type === "signature" && (
              <Box onClick={(e) => e.stopPropagation()} mb={12}>
                <Text
                  size="sm"
                  fw={"var(--font-weight-semi-bold,600)"}
                  c="var(--text-default,#363d4d)"
                  mb={4}
                >
                  Placeholder Text
                </Text>
                <Input
                  value={q.placeholder || ""}
                  onChange={(val) => updateQuestion(scope, q.id, { placeholder: val })}
                  placeholder="Click to sign"
                  size="small"
                />
              </Box>
            )}

            {q.type === "photo-upload" && (
              <Stack gap={8} mb={12} onClick={(e) => e.stopPropagation()}>
                <Group gap={8}>
                  <Text
                    size="sm"
                    fw={"var(--font-weight-semi-bold,600)"}
                    c="var(--text-default,#363d4d)"
                  >
                    Required Photos:
                  </Text>
                  <Group gap={4}>
                    <Button
                      type="icon"
                      variant="neutral-secondary"
                      size="small"
                      onClick={() => {
                        const s = q.photoSlots || [{ id: "1", label: "" }];
                        if (s.length > 1)
                          updateQuestion(scope, q.id, { photoSlots: s.slice(0, -1) });
                      }}
                    >
                      <Text size="sm" fw={"var(--font-weight-bold,700)"}>
                        −
                      </Text>
                    </Button>
                    <Text size="sm" fw={"var(--font-weight-semi-bold,600)"} w={24} ta="center">
                      {q.photoSlots?.length || 1}
                    </Text>
                    <Button
                      type="icon"
                      variant="neutral-secondary"
                      size="small"
                      iconLeft={<IconPlus size={12} />}
                      onClick={() => {
                        const s = q.photoSlots || [{ id: "1", label: "" }];
                        updateQuestion(scope, q.id, {
                          photoSlots: [...s, { id: `${Date.now()}`, label: "" }],
                        });
                      }}
                    />
                  </Group>
                </Group>
                {(q.photoSlots || [{ id: "1", label: "" }]).map((slot, si) => (
                  <Group key={slot.id} gap={6}>
                    <Box
                      w={22}
                      h={22}
                      style={{
                        borderRadius: "50%",
                        background: "var(--bg-neutral-medium,#f0f2f5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#646d7e",
                        flexShrink: 0,
                      }}
                    >
                      {si + 1}
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Input
                        value={slot.label}
                        placeholder="e.g. Front of ID, Receipt photo..."
                        size="small"
                        onChange={(val) => {
                          const s = [...(q.photoSlots || [])];
                          s[si] = { ...s[si], label: val };
                          updateQuestion(scope, q.id, { photoSlots: s });
                        }}
                      />
                    </Box>
                  </Group>
                ))}
                <Divider color="#e2e6eb" />
                <Group gap={8}>
                  <ToggleSwitch
                    checked={q.allowAdditionalPhotos || false}
                    onChange={(next) =>
                      updateQuestion(scope, q.id, { allowAdditionalPhotos: next })
                    }
                    size="sm"
                    intent="brand"
                  />
                  <Text size="sm" c="var(--text-default,#363d4d)">
                    Allow additional photos
                  </Text>
                </Group>
              </Stack>
            )}

            {q.type === "video-upload" && (
              <Stack gap={8} mb={12} onClick={(e) => e.stopPropagation()}>
                <Group gap={8}>
                  <Text
                    size="sm"
                    fw={"var(--font-weight-semi-bold,600)"}
                    c="var(--text-default,#363d4d)"
                  >
                    Required Videos:
                  </Text>
                  <Group gap={4}>
                    <Button
                      type="icon"
                      variant="neutral-secondary"
                      size="small"
                      onClick={() => {
                        const s = q.videoSlots || [{ id: "1", label: "" }];
                        if (s.length > 1)
                          updateQuestion(scope, q.id, { videoSlots: s.slice(0, -1) });
                      }}
                    >
                      <Text size="sm" fw={"var(--font-weight-bold,700)"}>
                        −
                      </Text>
                    </Button>
                    <Text size="sm" fw={"var(--font-weight-semi-bold,600)"} w={24} ta="center">
                      {q.videoSlots?.length || 1}
                    </Text>
                    <Button
                      type="icon"
                      variant="neutral-secondary"
                      size="small"
                      iconLeft={<IconPlus size={12} />}
                      onClick={() => {
                        const s = q.videoSlots || [{ id: "1", label: "" }];
                        updateQuestion(scope, q.id, {
                          videoSlots: [...s, { id: `${Date.now()}`, label: "" }],
                        });
                      }}
                    />
                  </Group>
                </Group>
                {(q.videoSlots || [{ id: "1", label: "" }]).map((slot, si) => (
                  <Group key={slot.id} gap={6}>
                    <Box
                      w={22}
                      h={22}
                      style={{
                        borderRadius: "50%",
                        background: "var(--bg-neutral-medium,#f0f2f5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#646d7e",
                        flexShrink: 0,
                      }}
                    >
                      {si + 1}
                    </Box>
                    <Box style={{ flex: 1 }}>
                      <Input
                        value={slot.label}
                        placeholder="e.g. Product demo, Walk-through..."
                        size="small"
                        onChange={(val) => {
                          const s = [...(q.videoSlots || [])];
                          s[si] = { ...s[si], label: val };
                          updateQuestion(scope, q.id, { videoSlots: s });
                        }}
                      />
                    </Box>
                  </Group>
                ))}
                <Divider color="#e2e6eb" />
                <Group gap={8}>
                  <ToggleSwitch
                    checked={q.allowAdditionalVideos || false}
                    onChange={(next) =>
                      updateQuestion(scope, q.id, { allowAdditionalVideos: next })
                    }
                    size="sm"
                    intent="brand"
                  />
                  <Text size="sm" c="var(--text-default,#363d4d)">
                    Allow additional videos
                  </Text>
                </Group>
              </Stack>
            )}

            <Group gap={8} onClick={(e) => e.stopPropagation()}>
              <ToggleSwitch
                checked={!q.required}
                onChange={(next) => updateQuestion(scope, q.id, { required: !next })}
                size="sm"
                intent="brand"
              />
              <Text size="sm" c="var(--text-default,#363d4d)">
                Mark as optional
              </Text>
            </Group>

            {q.type === "repeating-group" && (
              <Box
                mt={12}
                className={styles.groupFieldsContainer}
                onClick={(e) => e.stopPropagation()}
              >
                <Group justify="space-between" wrap="nowrap" mb={8}>
                  <Text
                    size="xs"
                    fw={"var(--font-weight-semi-bold,600)"}
                    c="var(--text-strong,#131927)"
                    tt="uppercase"
                    lts="0.04em"
                  >
                    Group fields · repeated for every entry
                  </Text>
                  <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
                    {(q.fields || []).length} field{(q.fields || []).length === 1 ? "" : "s"}
                  </Text>
                </Group>
                <QuestionList
                  scope={{ pageId, sectionId, groupId: q.id }}
                  questions={q.fields || []}
                />
              </Box>
            )}
          </Box>
        </Flex>
        {q.conditionalLogicEnabled && <ConditionalLogicEditor question={q} scope={scope} />}
      </Paper>
      {deleteConfirmOpen && (
        <ConfirmDeleteFieldModal
          isOpen
          fieldName={q.question}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={() => {
            setDeleteConfirmOpen(false);
            deleteQuestion(scope, q.id);
          }}
        />
      )}
    </Group>
  );
}
